import { Fragment, useEffect, useState } from "react";
import type { Callout, Point, Utility } from "@cs2/contracts";
import type Konva from "konva";
import {
  Circle,
  Group,
  Image as KonvaImage,
  Layer,
  Line,
  Path,
  Rect,
  Text,
} from "react-konva";
import { grenadeGlyphTransform, grenadeGlyphs } from "../lib/grenade-paths.js";
import {
  effectRings,
  effectSpokes,
  overlay,
  mapThemeColor,
  utilityEffect,
  withAlpha,
} from "../lib/map-style.js";

const CALLOUT_FONT_FAMILY = "Permanent Marker";
const CALLOUT_FONT_SPEC = `400 16px "${CALLOUT_FONT_FAMILY}"`;
let calloutFontPromise: Promise<FontFace[]> | undefined;

function useCalloutFont(): string {
  const [ready, setReady] = useState(
    () =>
      typeof document === "undefined" ||
      !("fonts" in document) ||
      document.fonts.check(CALLOUT_FONT_SPEC),
  );

  useEffect(() => {
    if (ready || typeof document === "undefined" || !("fonts" in document))
      return;
    let active = true;
    calloutFontPromise ??= document.fonts.load(CALLOUT_FONT_SPEC);
    void calloutFontPromise.finally(() => {
      if (active) setReady(true);
    });
    return () => {
      active = false;
    };
  }, [ready]);

  // Der Hook erzwingt nach dem Laden einen React-/Konva-Redraw. Ohne diesen
  // bliebe Canvas-Text bis zur nächsten Interaktion in der Fallback-Schrift.
  return ready ? CALLOUT_FONT_FAMILY : "sans-serif";
}

function calloutRotation(label: string): number {
  let hash = 0;
  for (const character of label)
    hash = (hash * 31 + character.charCodeAt(0)) | 0;
  return ((Math.abs(hash) % 7) - 3) * 0.45;
}

/**
 * Geteilte Konva-Leaf-Marks der Taktikkarte — von Editor UND Quickview genutzt,
 * damit beide Stacks dieselben Symbole/Effekte/Bomben zeichnen (Hard Rule 2).
 * `scale` rechnet Karteneinheiten in Bildschirmpixel um (Editor/Quickview
 * setzen `glyphScale(stageWidth)`).
 */

/**
 * `exactOptionalPropertyTypes` verbietet ein explizites `undefined` an
 * Konva-Props — optionale Werte deshalb per Spread weglassen statt setzen.
 */
export function optional<K extends string, V>(
  key: K,
  value: V | null | undefined,
): Record<K, V> | Record<string, never> {
  return value === null || value === undefined
    ? {}
    : ({ [key]: value } as Record<K, V>);
}

/** Bomben-Marker für platziertes C4 — bewusst ohne Wurfbogen und Effektradius. */
export function BombMarker({
  color,
  caption,
  strong,
  scale,
}: {
  color: string;
  caption?: string;
  strong?: boolean;
  scale: number;
}) {
  const bomb = overlay.bomb;
  return (
    <>
      <Circle
        radius={overlay.utility.ringRadius * scale}
        fill="#101719"
        stroke={color}
        strokeWidth={
          (strong
            ? overlay.utility.ringStroke * 1.25
            : overlay.utility.ringStroke) * scale
        }
      />
      <GrenadeGlyph type="C4" color={color} scale={scale} />
      {caption ? (
        <Text
          text={caption}
          y={bomb.labelOffsetY * scale}
          x={(-bomb.captionWidth / 2) * scale}
          width={bomb.captionWidth * scale}
          align="center"
          fill={color}
          fontStyle="bold"
          fontSize={bomb.fontSize * scale}
        />
      ) : null}
    </>
  );
}

/**
 * Kartencallout als handgeschriebene Taktik-Annotation: Permanent Marker,
 * leicht unregelmäßiger Winkel und warme Kreidefarbe statt UI-Typografie.
 * Kontur + kurzer Schlagschatten halten die Schrift auf hellen und dunklen
 * Radarflächen lesbar. Auswahl zeigt sich nur per Textfarbe, damit Quickview
 * und Editor außerhalb des Bearbeitungszustands identisch bleiben.
 */
export function CalloutPin({
  x,
  y,
  label,
  scale,
  selected,
  interactive,
  onClick,
  onDblClick,
  onDragEnd,
}: {
  x: number;
  y: number;
  label: string;
  scale: number;
  selected?: boolean;
  interactive?: boolean;
  onClick?: (event: Konva.KonvaEventObject<MouseEvent>) => void;
  onDblClick?: (event: Konva.KonvaEventObject<MouseEvent>) => void;
  onDragEnd?: (event: Konva.KonvaEventObject<DragEvent>) => void;
}) {
  const callout = overlay.callout;
  const fontFamily = useCalloutFont();
  const displayLabel = label.toLocaleUpperCase("de-DE");
  const width = Math.min(
    callout.maxWidth,
    Math.max(
      callout.minWidth,
      displayLabel.length * callout.characterWidth + callout.paddingX,
    ),
  );
  return (
    <Group
      x={x}
      y={y}
      rotation={calloutRotation(label)}
      listening={Boolean(interactive)}
      draggable={Boolean(interactive)}
      // Pointer-Ereignisse bedienen Maus und Touch mit denselben Callout-Aktionen.
      {...optional("onPointerClick", onClick)}
      {...optional("onPointerDblClick", onDblClick)}
      {...optional("onDragEnd", onDragEnd)}
    >
      <Text
        text={displayLabel}
        x={(-width / 2) * scale}
        y={(-callout.height / 2) * scale}
        width={width * scale}
        height={callout.height * scale}
        align="center"
        verticalAlign="middle"
        wrap="none"
        ellipsis
        fill={selected ? mapThemeColor("--accent") : "#efe6cf"}
        stroke="#101719"
        strokeWidth={0.7 * scale}
        fontFamily={fontFamily}
        fontStyle="normal"
        fontSize={callout.fontSize * scale}
        letterSpacing={callout.letterSpacing * scale}
        shadowColor="#020405"
        shadowBlur={2.2 * scale}
        shadowOffsetX={0.7 * scale}
        shadowOffsetY={1.2 * scale}
        shadowOpacity={0.92}
        // Ohne diese Flags zeichnet Konva bei Text mit Fill + Stroke + Shadow
        // für JEDES Callout einen Stage-großen Buffer-Canvas. Bei 40–60
        // Labels kostet damit schon ein einzelner Layer-Draw mehrere Frames.
        // Der Schatten bleibt auf der Füllung erhalten; nur die Kontur wirft
        // keinen zusätzlichen Schatten.
        shadowForStrokeEnabled={false}
        perfectDrawEnabled={false}
      />
    </Group>
  );
}

/** Das Granatensymbol auf der dunklen Scheibe am Landepunkt. */
export function GrenadeGlyph({
  type,
  color,
  scale,
}: {
  type: Utility["type"];
  color: string;
  scale: number;
}) {
  const glyph = grenadeGlyphs[type];
  const transform = grenadeGlyphTransform(
    type,
    overlay.utility.glyphSize * scale,
  );
  return (
    <Group
      x={transform.offset}
      y={transform.offset}
      scaleX={transform.scale}
      scaleY={transform.scale}
      listening={false}
    >
      {glyph.paths.map((path) => (
        <Path
          key={path.d}
          data={path.d}
          fill={color}
          {...optional("opacity", path.opacity)}
          {...optional("fillRule", path.fillRule)}
        />
      ))}
    </Group>
  );
}

/** Wirkfläche der Granate — Form und Maße kommen aus `lib/map-style`. */
export function UtilityEffectShape({
  type,
  color,
  scale,
  areaScaleX = scale,
  areaScaleY = scale,
  areas,
}: {
  type: Utility["type"];
  color: string;
  scale: number;
  areaScaleX?: number;
  areaScaleY?: number;
  areas?: readonly { points: readonly Point[]; intensity: number }[] | null;
}) {
  const effect = utilityEffect(type);
  if (!effect.kind) return null;
  if (areas?.length) {
    return (
      <>
        {areas.map((area, index) => (
          <Line
            key={`physics-area-${index}`}
            points={area.points.flatMap((point) => [
              point.x * areaScaleX,
              point.y * areaScaleY,
            ])}
            closed
            fill={withAlpha(
              color,
              Math.min(0.35, 0.08 + area.intensity * 0.27),
            )}
            stroke={color}
            strokeWidth={effect.strokeWidth * scale}
            listening={false}
          />
        ))}
      </>
    );
  }
  return (
    <>
      <Circle
        radius={effect.radius * scale}
        {...optional(
          "fill",
          effect.fillOpacity ? withAlpha(color, effect.fillOpacity) : null,
        )}
        stroke={color}
        strokeWidth={effect.strokeWidth * scale}
        {...optional(
          "dash",
          effect.dash?.map((value) => value * scale),
        )}
        opacity={effect.opacity}
        listening={false}
      />
      {effectRings(effect).map((ring) => (
        <Circle
          key={ring.radius}
          radius={ring.radius * scale}
          stroke={color}
          strokeWidth={ring.strokeWidth * scale}
          {...optional(
            "dash",
            ring.dash?.map((value) => value * scale),
          )}
          opacity={ring.opacity * effect.opacity}
          listening={false}
        />
      ))}
      {effectSpokes(effect).map((spoke) => (
        <Line
          key={spoke.join()}
          points={spoke.map((value) => value * scale)}
          stroke={color}
          strokeWidth={effect.strokeWidth * scale}
          opacity={0.75 * effect.opacity}
          lineCap="round"
          listening={false}
        />
      ))}
    </>
  );
}

/** Lädt ein Radarbild als `HTMLImageElement` für `<KonvaImage>`. */
export function useImage(source: string | null) {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  useEffect(() => {
    if (!source) {
      setImage(null);
      return;
    }
    const next = new Image();
    next.src = source;
    next.onload = () => setImage(next);
    return () => {
      next.onload = null;
    };
  }, [source]);
  return image;
}

/** Radarbild + abdunkelnde Überlagerung — unterste Ebene in Editor und Quickview. */
export function MapBackgroundLayer({
  image,
  width,
  height,
}: {
  image: HTMLImageElement | null;
  width: number;
  height: number;
}) {
  return (
    <Layer opacity={0.72} listening={false}>
      {image ? (
        <KonvaImage image={image} width={width} height={height} />
      ) : null}
      <Rect width={width} height={height} fill="rgba(3,8,10,.18)" />
    </Layer>
  );
}

/** Alle Kartencallouts als eigene, nicht-interaktive Ebene. */
export function CalloutLayer({
  callouts,
  sx,
  sy,
  scale,
}: {
  callouts: Record<string, Callout>;
  sx: number;
  sy: number;
  scale: number;
}) {
  return (
    <Layer listening={false}>
      {Object.entries(callouts).map(([key, callout]) => (
        <CalloutPin
          key={key}
          x={callout.x * sx}
          y={callout.y * sy}
          label={callout.label}
          scale={scale}
        />
      ))}
    </Layer>
  );
}

/** Zerlegt einen Weg in Läufe gleicher Abdeckung (offen / unter einer Ebene). */
function coverageRuns(
  points: readonly Point[],
  covered: readonly boolean[] | undefined,
): { points: readonly Point[]; covered: boolean }[] {
  if (!covered || points.length < 2) return [{ points, covered: false }];
  const runs: { points: Point[]; covered: boolean }[] = [];
  for (let index = 1; index < points.length; index += 1) {
    const isCovered = Boolean(covered[index - 1] && covered[index]);
    const last = runs.at(-1);
    if (last && last.covered === isCovered) last.points.push(points[index]!);
    else
      runs.push({
        points: [points[index - 1]!, points[index]!],
        covered: isCovered,
      });
  }
  return runs;
}

/**
 * Routenlinie: `"team"` eine einzelne gestrichelte Linie, `"own"` ein
 * gestapeltes Paar (dunkle Unterlinie + leuchtende Konturlinie), damit die
 * eigene Route auf jedem Untergrund erkennbar bleibt. Abschnitte unter einer
 * anderen Ebene (`covered`) werden gestrichelt und gedämpft. Keine
 * Kurvenglättung: der Laufweg ist bereits wandfrei, eine Spline würde ihn
 * wieder durch Ecken ziehen.
 */
export function RouteLine({
  variant,
  points,
  covered,
  sx,
  sy,
  scale,
  opacity = 1,
}: {
  variant: "own" | "team";
  points: readonly Point[];
  covered?: readonly boolean[];
  sx: number;
  sy: number;
  scale: number;
  opacity?: number;
}) {
  return (
    <>
      {coverageRuns(points, covered).map((run, index) => {
        const flat = run.points.flatMap((point) => [
          point.x * sx,
          point.y * sy,
        ]);
        const runOpacity =
          opacity * (run.covered ? overlay.route.coveredOpacity : 1);
        if (variant === "team")
          return (
            <Line
              key={index}
              points={flat}
              stroke="#d7dbd9"
              strokeWidth={overlay.route.teamStroke * scale}
              opacity={0.68 * runOpacity}
              dash={overlay.route.teamDash.map((value) => value * scale)}
              listening={false}
            />
          );
        const dash = run.covered
          ? overlay.route.coveredDash.map((value) => value * scale)
          : undefined;
        return (
          <Fragment key={index}>
            <Line
              points={flat}
              stroke="#101719"
              strokeWidth={overlay.route.ownUnderStroke * scale}
              opacity={runOpacity}
              {...(dash ? { dash } : {})}
              lineCap={dash ? "butt" : "round"}
              lineJoin="round"
              listening={false}
            />
            <Line
              points={flat}
              stroke={mapThemeColor("--route")}
              strokeWidth={overlay.route.ownStroke * scale}
              opacity={runOpacity}
              {...(dash ? { dash } : {})}
              lineCap={dash ? "butt" : "round"}
              lineJoin="round"
              listening={false}
            />
          </Fragment>
        );
      })}
    </>
  );
}

/** "Route verlässt die begehbare Fläche"-Warnmarker. */
export function BlockedMarker({
  x,
  y,
  scale,
}: {
  x: number;
  y: number;
  scale: number;
}) {
  return (
    <Group x={x} y={y} listening={false}>
      <Circle
        radius={overlay.routeDot.radius * 1.35 * scale}
        fill="#6e1d1d"
        stroke="#ff6b5f"
        strokeWidth={2 * scale}
      />
      <Text
        text="!"
        width={overlay.routeDot.radius * 2.7 * scale}
        height={overlay.routeDot.radius * 2.7 * scale}
        x={-overlay.routeDot.radius * 1.35 * scale}
        y={-overlay.routeDot.radius * 1.35 * scale}
        align="center"
        verticalAlign="middle"
        fill="#fff"
        fontStyle="bold"
        fontSize={overlay.routeDot.radius * 1.5 * scale}
      />
    </Group>
  );
}

/**
 * Ein Routenpunkt: nummeriert (Schritt) oder schlichter Punkt. `selected`
 * schaltet den größeren, grün konturierten Stil ein (nur Editor); `interactive`
 * verdrahtet Drag/Klick (nur Editor — Quickview lässt beides weg).
 */
export function StepMarker({
  x,
  y,
  scale,
  isNumbered,
  stepNumber,
  selected,
  opacity = 1,
  interactive,
  onDragEnd,
  onClick,
  onTap,
}: {
  x: number;
  y: number;
  scale: number;
  isNumbered: boolean;
  stepNumber?: number;
  selected?: boolean;
  opacity?: number;
  interactive?: boolean;
  onDragEnd?: (event: Konva.KonvaEventObject<DragEvent>) => void;
  onClick?: (event: Konva.KonvaEventObject<MouseEvent>) => void;
  onTap?: (event: Konva.KonvaEventObject<Event>) => void;
}) {
  return (
    <Group
      x={x}
      y={y}
      opacity={opacity}
      draggable={Boolean(interactive)}
      {...optional("onDragEnd", onDragEnd)}
      {...optional("onClick", onClick)}
      {...optional("onTap", onTap)}
    >
      {isNumbered ? (
        <>
          <Circle
            radius={
              (selected ? overlay.step.selectedRadius : overlay.step.radius) *
              scale
            }
            fill={mapThemeColor(selected ? "--accent" : "--route")}
            stroke={mapThemeColor("--bg")}
            strokeWidth={
              (selected ? overlay.step.selectedStroke : overlay.step.stroke) *
              scale
            }
            shadowBlur={0}
            shadowColor={mapThemeColor("--route")}
          />
          <Text
            text={String(stepNumber)}
            width={overlay.step.radius * 2 * scale}
            height={overlay.step.radius * 2 * scale}
            x={-overlay.step.radius * scale}
            y={-overlay.step.radius * scale}
            align="center"
            verticalAlign="middle"
            fill={mapThemeColor("--bg")}
            fontStyle="bold"
            fontSize={overlay.step.fontSize * scale}
          />
        </>
      ) : (
        <Circle
          radius={overlay.routeDot.radius * scale}
          fill="#293033"
          stroke={mapThemeColor("--route")}
          strokeWidth={overlay.routeDot.stroke * scale}
        />
      )}
    </Group>
  );
}

/** Badge am Routenende eines Mitspielers (grauer Kreis mit Slot-Buchstabe). */
export function TeamPlayerBadge({
  slot,
  x,
  y,
  scale,
}: {
  slot: string;
  x: number;
  y: number;
  scale: number;
}) {
  return (
    <Group x={x} y={y} listening={false}>
      <Circle
        radius={overlay.teamPlayer.radius * scale}
        fill="#293033"
        stroke="#fff"
        strokeWidth={overlay.teamPlayer.stroke * scale}
      />
      <Text
        text={slot}
        width={overlay.teamPlayer.radius * 2 * scale}
        height={overlay.teamPlayer.radius * 2 * scale}
        x={-overlay.teamPlayer.radius * scale}
        y={-overlay.teamPlayer.radius * scale}
        align="center"
        verticalAlign="middle"
        fill="#fff"
        fontStyle="bold"
        fontSize={overlay.teamPlayer.fontSize * scale}
      />
    </Group>
  );
}

/**
 * Geworfene Utility: Flugbahn (immer passiv, absolute Stage-Koordinaten) +
 * Landung (Wirkfläche, Ring, Symbol, Label — relativ zum Landepunkt
 * gruppiert). Quickview hängt hier per `groupRef` ihre Wurf-/Blüh-Animation
 * ein; der Editor macht die Landungsgruppe stattdessen draggable/klickbar
 * (`interactive`) — beide teilen sich denselben visuellen Kern (Hard Rule 2).
 */
export function ThrownUtilityLanding({
  utility,
  color,
  scale,
  sx,
  sy,
  trajectory,
  landing,
  areas = null,
  opacity = 1,
  groupRef,
  interactive,
  draggable,
  onDragStart,
  onDragEnd,
  onClick,
  onTap,
}: {
  utility: Utility;
  color: string;
  scale: number;
  sx: number;
  sy: number;
  trajectory: readonly Point[];
  landing: Point;
  areas?: readonly { points: readonly Point[]; intensity: number }[] | null;
  opacity?: number;
  groupRef?: React.Ref<Konva.Group>;
  /** Rendert die vergrößerte, unsichtbare Trefferfläche (nur Editor nötig). */
  interactive?: boolean;
  draggable?: boolean;
  onDragStart?: (event: Konva.KonvaEventObject<DragEvent>) => void;
  onDragEnd?: (event: Konva.KonvaEventObject<DragEvent>) => void;
  onClick?: (event: Konva.KonvaEventObject<MouseEvent>) => void;
  onTap?: (event: Konva.KonvaEventObject<Event>) => void;
}) {
  return (
    <>
      <Line
        points={trajectory.flatMap((point) => [point.x * sx, point.y * sy])}
        tension={trajectory.length > 3 ? 0 : 0.5}
        stroke={color}
        strokeWidth={overlay.utility.throwStroke * scale}
        dash={overlay.utility.throwDash.map((value) => value * scale)}
        opacity={0.7}
        listening={false}
      />
      <Group
        ref={groupRef}
        x={landing.x * sx}
        y={landing.y * sy}
        opacity={opacity}
        draggable={Boolean(draggable)}
        {...optional("onDragStart", onDragStart)}
        {...optional("onDragEnd", onDragEnd)}
        {...optional("onClick", onClick)}
        {...optional("onTap", onTap)}
      >
        {interactive ? (
          <Circle
            radius={Math.max(24, overlay.utility.ringRadius) * scale}
            fill="rgba(0,0,0,0.001)"
          />
        ) : null}
        <UtilityEffectShape
          type={utility.type}
          color={color}
          scale={scale}
          areaScaleX={sx}
          areaScaleY={sy}
          areas={areas}
        />
        <Circle
          radius={overlay.utility.ringRadius * scale}
          fill="#101719"
          stroke={color}
          strokeWidth={overlay.utility.ringStroke * scale}
        />
        <GrenadeGlyph type={utility.type} color={color} scale={scale} />
        <Text
          text={utility.type}
          y={overlay.utility.labelOffsetY * scale}
          x={-35 * scale}
          width={70 * scale}
          align="center"
          fill={color}
          fontStyle="bold"
          fontSize={overlay.utility.labelFontSize * scale}
        />
      </Group>
    </>
  );
}
